import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";

import NotFoundPage from "components/error/NotFoundPage";
import SpaceliftLoader from "components/loading/SpaceliftLoader";
import SystemMessage from "components/SystemMessage";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Link from "ds/components/Link";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import useIsFlowsIntegrationEnabled from "hooks/useIsFlowsIntegrationEnabled";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";
import { WarningTriangleColored } from "components/icons/generated";

import { GET_FLOWS_INSTANCES } from "./gql";

const FlowsLinkOrganizations = () => {
  useTitle("Enable Flows");

  const isFlowsIntegrationEnabled = useIsFlowsIntegrationEnabled();
  const { viewer } = useTypedContext(AccountContext);
  const isAvailable = isFlowsIntegrationEnabled && viewer.admin;

  const { data, loading } = useQuery(GET_FLOWS_INSTANCES, { skip: !isAvailable });
  const [aiTermsAccepted, setAiTermsAccepted] = useState(false);

  // flowsInstances returns full URLs, but /flows/link's allowlist is keyed by bare
  // hostname, so the instance value is the host (the domain), not the full redirectURL.
  // A single instance is trusted per environment, so there is nothing to pick.
  const instanceHost = useMemo(() => {
    const [instance] = data?.flowsInstances ?? [];

    if (!instance) {
      return null;
    }

    try {
      return new URL(instance.redirectURL).hostname;
    } catch {
      return null;
    }
  }, [data?.flowsInstances]);

  if (loading) {
    return <SpaceliftLoader />;
  }

  if (!isAvailable) {
    return <NotFoundPage />;
  }

  if (!instanceHost) {
    return (
      <SystemMessage
        title="No Flows instances available"
        message="There are no Flows instances available to link to."
        icon={WarningTriangleColored}
      />
    );
  }

  const handleEnableFlows = () => {
    window.location.href = `/flows/link?instance=${encodeURIComponent(instanceHost)}`;
  };

  return (
    <SystemMessage
      title="Enable Flows"
      message={
        <>
          By continuing, you will enable{" "}
          <Link href={getDocsUrl("/flows/introduction")} target="_blank" size="inherit">
            Spacelift Flows
          </Link>{" "}
          for your Spacelift Deploy account.
        </>
      }
    >
      <Box direction="column" gap="xx-large" fullWidth>
        <Toggle
          variant="checkbox"
          isSelected={aiTermsAccepted}
          onChange={setAiTermsAccepted}
          aria-label="Accept Spacelift Intelligence Terms of Use"
        >
          <Typography tag="span" variant="p-body3">
            I acknowledge that I have read and accept the{" "}
            <Link href={getDocsUrl("/legal/ai-terms")} target="_blank" size="inherit">
              Spacelift Intelligence Terms of Use
            </Link>
          </Typography>
        </Toggle>

        <Button variant="primary" onPress={handleEnableFlows} disabled={!aiTermsAccepted} fullWidth>
          Continue
        </Button>
      </Box>
    </SystemMessage>
  );
};

export default FlowsLinkOrganizations;
